/**
 * Utilities for post-login redirect (return URL) with open-redirect protection.
 * Single source of truth for validation so all entry points (ProtectedRoute, axios, Login) stay consistent.
 */

/** Query param name for the post-login return path (e.g. /auth?from=/groups). */
export const REDIRECT_QUERY_PARAM = 'from';

/**
 * Returns a safe path to redirect to after login, or the default path if the candidate is invalid.
 * Prevents open-redirect by allowing only same-origin relative paths.
 *
 * Allowed: "/groups", "/device-details/?id=123"
 * Rejected: "https://evil.com", "//evil.com", "/auth", "/login", "/path/../etc", paths with backslash
 */
export function getSafeRedirectPath(
  candidate: string | null | undefined,
  defaultPath: string
): string {
  if (!candidate || typeof candidate !== 'string') {
    return defaultPath;
  }
  const trimmed = candidate.trim();
  if (!trimmed) return defaultPath;

  // Must be a relative path (same-origin)
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return defaultPath;
  }
  // No path traversal
  if (trimmed.includes('..')) {
    return defaultPath;
  }
  // No backslash (can be used in redirect bypass on some environments)
  if (trimmed.includes('\\')) {
    return defaultPath;
  }
  // Do not redirect back to auth flows (avoid loops)
  const isAuthPath =
    trimmed === '/auth' ||
    trimmed.startsWith('/auth/') ||
    trimmed.startsWith('/auth?') ||
    trimmed.startsWith('/auth#') ||
    trimmed === '/login' ||
    trimmed.startsWith('/login?') ||
    trimmed.startsWith('/login#');
  if (isAuthPath) {
    return defaultPath;
  }

  return trimmed;
}

/**
 * Builds the full path (pathname + search + hash) from a location-like object.
 * Used when persisting the return path to URL or state.
 */
export function getFullPath(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  return (
    location.pathname +
    (location.search ?? '') +
    (location.hash ?? '')
  );
}
